import { EndpointService, type APIPaginatedResponse } from "@/types/api/api";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";

/**
 * Interface representing the parameters for listing roles.
 * @interface IListParams
 * @property {number} [page] - The page number for pagination.
 * @property {number} [page_size] - The number of items per page.
 * @property {boolean} [isVenueSpecific] - Indicates if the roles should be specific to a venue.
 */
export interface IListParams {
  page?: number;
  page_size?: number;
  isVenueSpecific?: boolean;
}

/**
 * Interface representing a compact role.
 * @interface IRoleCompact
 * @property {number} id - The unique identifier for the role.
 * @property {string} name - The name of the role.
 */
export interface IRoleCompact {
  id: number;
  name: string;
}

/**
 * Interface representing a role with permissions.
 * @interface IRole
 * @extends {IRoleCompact}
 * @property {Array<string>} permissions - The list of permissions associated with the role.
 * @property {string} [description] - Optional description of the role.
 * @property {boolean} [is_admin] - Indicates if the role is an admin role.
 */
export interface IRole extends IRoleCompact {
  permissions: Array<string>;
  description?: string;
  is_admin?: boolean;
}

/**
 * Interface representing a paginated list of roles.
 * @interface IRolesList
 * @extends {APIPaginatedResponse<IRole>}
 * This interface extends the APIPaginatedResponse to include a list of roles.
 */
export interface IRolesList extends APIPaginatedResponse<IRole> {}

/**
 * Interface representing the roles service.
 * @interface IRolesService
 * @property {function} list - A function that retrieves a list of roles based on the provided parameters.
 * @param {IListParams} params - The parameters for listing roles, including pagination and venue specificity.
 * @returns {UseQueryResult<IRolesList, Error>} - The result of the query containing the roles list or an error.
 */
export interface IRolesService {
  list: (params: IListParams) => UseQueryResult<IRolesList, Error>;
  dataSourceListQuery: (
    params: IListParams,
  ) => Promise<AxiosResponse<IRolesList>>;
}

/**
 * Class representing the roles service implementation.
 * @class RolesService
 * @implements {IRolesService}
 */
export class RolesService implements IRolesService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(params: IListParams): UseQueryResult<IRolesList, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["roles", "list"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { isVenueSpecific = false, ...restParams } = updatedParams;
        const { data } = await this.api.get<AxiosResponse<IRolesList>>(
          "/personnel/roles/",
          {
            params: restParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
          },
        );

        return data;
      },
    });
  }

  /**
   * Method to directly query roles without using React Query.
   * @param {IListParams} params - The parameters for querying roles, including pagination and venue specificity.
   * @returns {Promise<AxiosResponse<IRolesList>>} The API response containing the roles list.
   */
  async dataSourceListQuery(
    params: IListParams,
  ): Promise<AxiosResponse<IRolesList>> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value !== null && value !== undefined && value !== "",
      ),
    );

    return this.api.get<AxiosResponse<IRolesList>>("/personnel/roles/", {
      params: updatedParams,
      endpointService: EndpointService.Gateway,
    });
  }
}
